import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { FileSpreadsheet, History, Languages, ScrollText } from 'lucide-react';
import { useSearchParams } from 'react-router-dom';

import {
  TRANSLATIONS_PAGE_LIMIT,
  loadTranslationCatalogue,
  loadTranslationEdits,
  loadTranslations,
  saveTranslation,
} from './api';
import {
  Empty, Failed, Head, Loading, SearchField, XomicsSelect,
} from './parts';
import TranslationEventLog from './TranslationEventLog';
import TranslationImport from './TranslationImport';
import { Field, errorMessage, fieldAria } from './ui';
import { wordDiff } from './wordDiff';
import { tx } from './i18n';
import { codeLabel } from './labels';

const SEARCH_DEBOUNCE_MS = 400;

function when(value) {
  return new Date(value).toLocaleString(undefined, {
    day: '2-digit', month: 'short', hour: '2-digit', minute: '2-digit',
  });
}

// Only what changed is marked, so a one-word edit reads as a one-word edit.
function Diff({ before, after }) {
  const parts = useMemo(() => wordDiff(before, after), [before, after]);
  return (
    <p className="xo-translation-diff">
      {parts.map((part, index) => {
        const key = `${index}-${part.type}`;
        if (part.type === 'del') return <del key={key}>{part.text}</del>;
        if (part.type === 'ins') return <ins key={key}>{part.text}</ins>;
        return <span key={key}>{part.text}</span>;
      })}
    </p>
  );
}

// One row. The key is text, never an input: keys belong to the report template,
// and this screen has no route that adds or removes one.
function Row({ row, draft, saving, error, target, refFor, onChange, onSave }) {
  const id = `translation-${row.id}`;
  const dirty = draft !== row.value;

  return (
    <li className={target ? 'xo-translation is-target' : 'xo-translation'} ref={refFor}>
      <p className="xo-translation-key">{row.translationKey}</p>

      {row.reference !== null && (
        <p className="xomics-field-hint" id={`${id}-reference`}>{row.reference}</p>
      )}

      <label className="xo-hidden" htmlFor={id}>{tx("ui.translations.value_for_translationkey_fec27fe5", { translationKey: row.translationKey })}</label>
      <textarea
        id={id}
        value={draft}
        rows={draft.length > 120 ? 4 : 2}
        onChange={(event) => onChange(row.id, event.target.value)}
        aria-describedby={row.reference !== null ? `${id}-reference` : undefined}
        aria-invalid={error ? true : undefined}
        disabled={saving}
      />

      <div className="xo-translation-foot">
        <button type="button" className="xo-btn-2" disabled={!dirty || saving}
          onClick={() => onSave(row.id)}>
          {saving ? tx("ui.translations.saving_56a2285c") : tx("ui.translations.save_efc007a3")}
        </button>
        {error && <span className="xomics-field-error" role="alert">{error}</span>}
      </div>
    </li>
  );
}

// Every edit, newest first. Restoring writes into the draft rather than saving,
// so the person sees what they are replacing and the restore is itself recorded
// as an ordinary edit.
function Versions({ edits, loading, error, onRetry, onOpen, onRestore, canRestore }) {
  if (loading) return <Loading label={tx("ui.translations.reading_the_edit_history_301e3077")} />;
  if (error) return <Failed message={error} onRetry={onRetry} />;
  if (edits.length === 0) return <Empty title={tx("ui.translations.no_edits_match_a4a14c73")} />;

  return (
    <ol className="xo-versions">
      {edits.map((edit) => (
        <li key={edit.id}>
          <p className="xomics-field-hint">{edit.who} · {when(edit.at)}</p>
          <button type="button" className="xo-link xo-version-key"
            onClick={() => onOpen(edit)}>
            {edit.contentType} · {edit.language} · {edit.translationKey}
          </button>
          <Diff before={edit.before} after={edit.after} />
          {/* Restoring writes into a row's draft, so it only appears once that
              row is on screen -- however it got there. */}
          {canRestore(edit) ? (
            <button type="button" className="xo-link" onClick={() => onRestore(edit)}>
              {tx("ui.translations.restore_this_wording_88268a9b")}
            </button>
          ) : (
            <p className="xomics-field-hint">{tx("ui.translations.click_the_key_to_open_it_or_navigate_via_the_filter_8d0f9092")}</p>
          )}
        </li>
      ))}
    </ol>
  );
}

// Wording on reports and in the app, by content type and language.
//
// Values only. A missing key is a provisioning job, not something to create here.
export default function XomicsTranslations() {
  const [searchParams] = useSearchParams();
  const [catalogue, setCatalogue] = useState({ contentTypes: [], languages: [] });
  const [catalogueError, setCatalogueError] = useState('');
  const [catalogueLoading, setCatalogueLoading] = useState(true);

  const [contentTypeId, setContentTypeId] = useState(() => searchParams.get('contentTypeId') || '');
  const [languageCode, setLanguageCode] = useState(() => searchParams.get('languageCode') || '');
  const [term, setTerm] = useState('');
  const [debouncedTerm, setDebouncedTerm] = useState('');

  const [rows, setRows] = useState([]);
  const [total, setTotal] = useState(0);
  const [offset, setOffset] = useState(0);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  // Keyed by row id, so saving or paging never discards text typed elsewhere.
  const [drafts, setDrafts] = useState({});
  const [savingId, setSavingId] = useState(null);
  const [rowErrors, setRowErrors] = useState({});
  const [status, setStatus] = useState('');

  const [historyOpen, setHistoryOpen] = useState(false);
  const [panel, setPanel] = useState('');
  const [edits, setEdits] = useState([]);
  const [editsLoading, setEditsLoading] = useState(false);
  const [editsError, setEditsError] = useState('');
  const [editsTerm, setEditsTerm] = useState('');
  const [editsBeforeId, setEditsBeforeId] = useState(null);
  const [editsMore, setEditsMore] = useState(false);
  const [targetId, setTargetId] = useState(null);
  // Set when a history entry is opened. The filters change first, so the scroll
  // has to wait for the row it names to actually be on the page.
  const [pendingTarget, setPendingTarget] = useState(null);

  const rowNodes = useRef({});

  const loadCatalogue = useCallback(async () => {
    setCatalogueLoading(true);
    setCatalogueError('');
    try {
      setCatalogue(await loadTranslationCatalogue());
    } catch (requestError) {
      setCatalogueError(errorMessage(requestError));
    } finally {
      setCatalogueLoading(false);
    }
  }, []);

  useEffect(() => { loadCatalogue(); }, [loadCatalogue]);

  useEffect(() => {
    const timer = setTimeout(() => setDebouncedTerm(term.trim()), SEARCH_DEBOUNCE_MS);
    return () => clearTimeout(timer);
  }, [term]);

  const ready = contentTypeId !== '' && languageCode !== '';

  const fetchPage = useCallback(async (nextOffset) => {
    if (contentTypeId === '' || languageCode === '') return;
    setLoading(true);
    setError('');
    try {
      const page = await loadTranslations({
        contentTypeId,
        languageCode,
        q: debouncedTerm,
        limit: TRANSLATIONS_PAGE_LIMIT,
        offset: nextOffset,
      });
      setRows(page.translations);
      setTotal(page.total);
      setOffset(nextOffset);
      setDrafts(Object.fromEntries(page.translations.map((row) => [row.id, row.value])));
      setRowErrors({});
      rowNodes.current = {};
    } catch (requestError) {
      setError(errorMessage(requestError));
    } finally {
      setLoading(false);
    }
  }, [contentTypeId, languageCode, debouncedTerm]);

  // A new content type, language or search term starts from the first page.
  useEffect(() => { setStatus(''); fetchPage(0); }, [fetchPage]);

  const fetchEdits = useCallback(async () => {
    setEditsLoading(true);
    setEditsError('');
    try {
      const page = await loadTranslationEdits({ limit: 100 });
      setEdits(page.edits);
      setEditsBeforeId(page.nextBeforeId);
    } catch (requestError) {
      setEditsError(errorMessage(requestError));
    } finally {
      setEditsLoading(false);
    }
  }, []);

  const olderEdits = useCallback(async () => {
    if (editsBeforeId === null) return;
    setEditsMore(true);
    try {
      const page = await loadTranslationEdits({ limit: 100, beforeId: editsBeforeId });
      setEdits((current) => [...current, ...page.edits]);
      setEditsBeforeId(page.nextBeforeId);
    } catch (requestError) {
      setEditsError(errorMessage(requestError));
    } finally {
      setEditsMore(false);
    }
  }, [editsBeforeId]);

  // Filtering only ever covers what has been loaded, which is what the footer
  // says -- a count over the whole log would be a lie about a paged read.
  const shownEdits = useMemo(() => {
    const needle = editsTerm.trim().toLowerCase();
    if (needle === '') return edits;
    return edits.filter((edit) => [
      edit.translationKey, edit.contentType, edit.language,
      edit.who, edit.before, edit.after,
    ].some((value) => value !== null && String(value).toLowerCase().includes(needle)));
  }, [edits, editsTerm]);

  const selected = useMemo(
    () => catalogue.contentTypes.find((type) => String(type.id) === contentTypeId) || null,
    [catalogue.contentTypes, contentTypeId],
  );

  const dirtyCount = useMemo(
    () => rows.filter((row) => drafts[row.id] !== undefined && drafts[row.id] !== row.value).length,
    [rows, drafts],
  );

  const loadedIds = useMemo(() => new Set(rows.map((row) => row.id)), [rows]);
  const canRestore = useCallback((edit) => loadedIds.has(edit.translationId), [loadedIds]);

  // Prefer the recorded id; fall back to the display name for entries written
  // before the id was audited.
  const contentTypeIdFor = useCallback((edit) => {
    const recorded = Number(edit.contentTypeId);
    if (Number.isInteger(recorded) && recorded > 0) return String(recorded);
    const byName = catalogue.contentTypes.find((type) => type.displayName === edit.contentType);
    return byName ? String(byName.id) : '';
  }, [catalogue.contentTypes]);

  function updateDraft(id, value) {
    setDrafts((current) => ({ ...current, [id]: value }));
  }

  function toggleHistory() {
    const next = !historyOpen;
    setHistoryOpen(next);
    if (next) setPanel('');
    if (next && edits.length === 0 && !editsLoading) fetchEdits();
  }

  function openPanel(name) {
    setPanel((current) => (current === name ? '' : name));
    setHistoryOpen(false);
  }

  // Filter to the entry's content type, language and key, then scroll to it once
  // it loads. Searching by the key is what guarantees the row is on the first
  // page rather than somewhere in several hundred.
  function openEdit(edit) {
    const nextContentTypeId = contentTypeIdFor(edit);
    if (nextContentTypeId === '') {
      setStatus(tx('ui.translations.entry_unavailable', {
        contentType: edit.contentType,
        translationKey: edit.translationKey,
      }));
      return;
    }

    const already = rowNodes.current[edit.translationId];
    if (already && String(contentTypeId) === nextContentTypeId && languageCode === edit.language) {
      setTargetId(edit.translationId);
      already.scrollIntoView({ behavior: 'smooth', block: 'center' });
      return;
    }

    setContentTypeId(nextContentTypeId);
    setLanguageCode(edit.language);
    setTerm(edit.translationKey);
    setDebouncedTerm(edit.translationKey);
    setTargetId(null);
    setPendingTarget(edit.translationId);
  }

  function restore(edit) {
    updateDraft(edit.translationId, edit.before ?? '');
    openEdit(edit);
    setStatus(tx('ui.translations.wording_restored', {
      translationKey: edit.translationKey,
    }));
  }

  async function save(id) {
    const row = rows.find((candidate) => candidate.id === id);
    if (!row) return;
    setSavingId(id);
    setRowErrors((current) => ({ ...current, [id]: undefined }));
    setStatus('');
    try {
      const { translation, unchanged } = await saveTranslation(id, drafts[id]);
      if (translation) {
        setRows((current) => current.map((candidate) => (candidate.id === id
          ? { ...candidate, value: translation.value, updatedAt: translation.updatedAt }
          : candidate)));
      }
      setStatus(unchanged
        ? tx('ui.translations.no_change_to_save')
        : tx('ui.translations.translation_saved', { translationKey: row.translationKey }));
      // The history says something different, so it is refetched rather than
      // left showing a version that is no longer the latest.
      if (!unchanged && historyOpen) fetchEdits();
    } catch (requestError) {
      setRowErrors((current) => ({ ...current, [id]: errorMessage(requestError) }));
    } finally {
      setSavingId(null);
    }
  }

  useEffect(() => {
    if (pendingTarget === null || loading) return;
    const node = rowNodes.current[pendingTarget];
    if (!node) {
      if (rows.length > 0) setPendingTarget(null);
      return;
    }
    setTargetId(pendingTarget);
    setPendingTarget(null);
    node.scrollIntoView({ behavior: 'smooth', block: 'center' });
  }, [pendingTarget, loading, rows]);

  const to = Math.min(offset + rows.length, total);

  return (
    <div className="xo-platform-page xo-translations-page">
      <Head
        title={tx("ui.translations.translations_8ad8302d")}
        subtitle={tx("ui.translations.wording_on_reports_and_in_the_app_values_only_88e326d7")}
      >
        <button type="button" className="xo-btn-2" aria-expanded={panel === 'import'}
          onClick={() => openPanel('import')}>
          <FileSpreadsheet aria-hidden="true" /> Spreadsheet
        </button>
        <button type="button" className="xo-btn-2" aria-expanded={panel === 'events'}
          onClick={() => openPanel('events')}>
          <ScrollText aria-hidden="true" /> Event log
        </button>
        <button type="button" className="xo-btn-2" aria-expanded={historyOpen}
          onClick={toggleHistory}>
          <History aria-hidden="true" /> {historyOpen ? tx("ui.translations.hide_history_f752d72c") : tx("ui.translations.history_90ccd649")}
        </button>
      </Head>

      {panel === 'import' && (
        <section className="xo-translation-history">
          <div className="xo-section-heading">
            <div>
              <h2>Bulk edit by spreadsheet</h2>
              <p>Export, edit the translation column, upload, check the diff, then confirm.</p>
            </div>
          </div>
          <div className="xo-data-surface is-padded">
            <TranslationImport
              contentTypeId={contentTypeId}
              languageCode={languageCode}
              label={selected ? selected.code || selected.displayName : ''}
              onApplied={() => fetchPage(offset)}
            />
          </div>
        </section>
      )}

      {panel === 'events' && (
        <section className="xo-translation-history">
          <div className="xo-section-heading">
            <div>
              <h2>Event log</h2>
              <p>Every edit across all content types and languages, newest first. Rolling one back records a new event rather than erasing anything.</p>
            </div>
          </div>
          <div className="xo-data-surface is-padded">
            <TranslationEventLog onRolledBack={() => fetchPage(offset)} />
          </div>
        </section>
      )}

      {catalogueLoading && <Loading label={tx("ui.translations.reading_content_types_e0c900f0")} />}
      {catalogueError && <Failed message={catalogueError} onRetry={loadCatalogue} />}

      {historyOpen && (
        <section className="xo-translation-history">
          <div className="xo-section-heading">
            <div><h2>{tx("ui.translations.edit_history_528df636")}</h2><p>{tx("ui.translations.open_any_key_to_inspect_or_restore_its_wording_276945ca")}</p></div>
            <SearchField value={editsTerm} onChange={setEditsTerm}
              placeholder={tx("ui.translations.filter_history_76bff657")}
              label={tx("ui.translations.filter_the_edit_history_dfd8d7fe")} />
          </div>
          <div className="xo-data-surface is-padded">
            <Versions edits={shownEdits} loading={editsLoading} error={editsError}
              onRetry={fetchEdits} onOpen={openEdit} onRestore={restore} canRestore={canRestore} />
          </div>
          <div className="xo-foot-split">
            <span>{tx("ui.translations.showing_length_of_length2_loaded_3838e392", { length: shownEdits.length, length2: edits.length })}</span>
            {editsBeforeId !== null && (
              <button type="button" className="xo-btn-2" disabled={editsMore} onClick={olderEdits}>
                {editsMore ? tx("ui.translations.loading_33ce4174") : tx("ui.translations.load_100_older_98212a9b")}
              </button>
            )}
          </div>
        </section>
      )}

      {!catalogueLoading && !catalogueError && (
        <section className="xo-translation-picker">
          <div className="xo-translation-picker-panel">
            <div className="xo-translation-picker-intro">
              <span aria-hidden="true"><Languages /></span>
              <h2>{tx("ui.translations.choose_what_to_translate_d248e0b0")}</h2>
              <p>{tx("ui.translations.select_one_content_type_then_the_language_you_want_to__bdb8801f")}</p>
            </div>
            <div className="xo-translation-picker-fields">
              <Field
                id="translation-content-type"
                label={tx("ui.translations.content_type_de15f753")}
                hint={selected
                  ? tx("ui.translations.kind_keycount_keys_languagecount_languages_c523d4f6", { kind: codeLabel('contentKind', selected.kind), keyCount: selected.keyCount, languageCount: selected.languageCount })
                  : tx("ui.translations.reports_and_the_app_interface_218fb5c0")}
              >
                <XomicsSelect
                  {...fieldAria('translation-content-type', { hint: 'x' })}
                  value={contentTypeId}
                  onChange={(event) => setContentTypeId(event.target.value)}
                >
                  <option value="">{tx("ui.translations.select_a_content_type_b0ceb041")}</option>
                  {catalogue.contentTypes.map((type) => (
                    <option key={type.id} value={type.id}>
                      {type.displayName} ({codeLabel('contentKind', type.kind)})
                    </option>
                  ))}
                </XomicsSelect>
              </Field>

              <span className="xo-translation-picker-step" aria-hidden="true" />

              <Field
                id="translation-language"
                label={tx("ui.translations.language_89b86ab0")}
                hint={ready && !loading && total === 0
                  ? tx("ui.translations.nothing_translated_for_this_language_yet_3a50218a")
                  : undefined}
              >
                <XomicsSelect
                  {...fieldAria('translation-language', {
                    hint: ready && !loading && total === 0 ? 'x' : undefined,
                  })}
                  value={languageCode}
                  onChange={(event) => setLanguageCode(event.target.value)}
                >
                  <option value="">{tx("ui.translations.select_a_language_84941c4d")}</option>
                  {catalogue.languages.map((language) => (
                    <option key={language.code} value={language.code}>
                      {language.nativeName
                        ? tx("ui.translations.name_nativename_aca475e0", { name: language.name, nativeName: language.nativeName })
                        : language.name}
                    </option>
                  ))}
                </XomicsSelect>
              </Field>
            </div>
          </div>
        </section>
      )}

      {ready && (
        <section className="xo-translation-workspace">
          <div className="xo-section-heading">
            <div><h2>{selected ? selected.displayName : tx("ui.translations.translations_8ad8302d")}</h2>
              <p>{languageCode ? tx("ui.translations.editing_value_total_entries_e89e84ed", { value: languageCode.toUpperCase(), total: total }) : tx("ui.translations.select_a_language_bc06fdbb")}</p></div>
            <SearchField value={term} onChange={setTerm}
              placeholder={tx("ui.translations.filter_by_key_or_wording_ebe936a3")}
              label={tx("ui.translations.filter_translations_53f7350c")} />
          </div>

          {status && <p className="xo-alert t-ok" role="status">{status}</p>}
          <div className="xo-data-surface xo-translation-results">
            {loading && <Loading label={tx("ui.translations.reading_translations_51ac7992")} />}
            {error && <Failed message={error} onRetry={() => fetchPage(offset)} />}

            {!loading && !error && rows.length === 0 && (
              <Empty title={tx("ui.translations.no_translations_here_0e52e667")}
                detail={tx("ui.translations.this_content_type_has_nothing_in_this_language_or_noth_176eaa8b")} />
            )}

            {!loading && !error && rows.length > 0 && (
              <ul className="xo-translations">
                {rows.map((row) => (
                  <Row key={row.id} row={row} draft={drafts[row.id] ?? row.value ?? ''}
                    saving={savingId === row.id} error={rowErrors[row.id]}
                    target={targetId === row.id} refFor={(node) => { rowNodes.current[row.id] = node; }}
                    onChange={updateDraft} onSave={save} />
                ))}
              </ul>
            )}
          </div>
          <div className="xo-foot-split xo-translation-pagination">
            <span>{total === 0 ? tx("ui.translations.nothing_to_show_901b56b2") : tx("ui.translations.showing_value_to_of_total_f3486640", { value: offset + 1, to: to, total: total })}
              {dirtyCount > 0 && tx("ui.translations.dirtycount_unsaved_37bb1a8b", { dirtyCount: dirtyCount })}</span>
            <span className="xo-bar-group">
              <button type="button" className="xo-btn-2" disabled={loading || offset === 0}
                onClick={() => fetchPage(Math.max(0, offset - TRANSLATIONS_PAGE_LIMIT))}>{tx("ui.translations.previous_50f94286")}</button>
              <button type="button" className="xo-btn-2" disabled={loading || to >= total}
                onClick={() => fetchPage(offset + TRANSLATIONS_PAGE_LIMIT)}>{tx("ui.translations.next_bc981983")}</button>
            </span>
          </div>
        </section>
      )}
    </div>
  );
}
